import React, { useState } from "react";
import {
  Drawer,
  Button,
  Space,
  Typography,
  Spin,
  theme,
  ButtonProps,
  DrawerProps,
} from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";

const { Text } = Typography;

export interface AppDrawerProps extends Omit<
  DrawerProps,
  "title" | "footer" | "extra"
> {
  /** Main title displayed in the drawer header */
  title?: React.ReactNode;
  /** Optional subtitle or description displayed below the title */
  subtitle?: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Callback fired when drawer requests closing */
  onClose?: (e?: React.MouseEvent | React.KeyboardEvent) => void;
  /** Primary save / submit callback (supports async with automatic loading state) */
  onSave?: () => void | Promise<any>;
  /** Save button label (default: "Save") */
  saveText?: React.ReactNode;
  /** Props forwarded to the save button */
  saveButtonProps?: ButtonProps;
  /** Cancel button label (default: "Cancel") */
  cancelText?: React.ReactNode;
  /** Props forwarded to the cancel button */
  cancelButtonProps?: ButtonProps;
  /** Whether to show the cancel button in footer (default: true) */
  showCancel?: boolean;
  /** Custom actions rendered on the left side of footer */
  footerLeft?: React.ReactNode;
  /** Additional custom actions rendered in footer before Save/Cancel */
  extraFooter?: React.ReactNode;
  /** Complete override for footer (pass null to hide footer) */
  footer?: React.ReactNode | null;
  /** Extra header elements placed alongside the close icon on top right */
  extra?: React.ReactNode;
  /** Drawer width (default: 560) */
  width?: number | string;
  /** Shows a full-body loading spinner */
  loading?: boolean;
  /** Disable default content body padding (default: false) */
  noPadding?: boolean;
  /** Custom body padding (default: "20px 24px") */
  bodyPadding?: number | string;
  /** Drawer content */
  children?: React.ReactNode;
}

/**
 * Standard reusable Drawer component for the application.
 *
 * Features:
 * - Header with title, subtitle, and top-right close button.
 * - Sticky footer with right-aligned Save & Cancel action buttons.
 * - Automatic async loading management on save.
 * - Full-body loading state support.
 * - Configurable width, padding, and custom footer/extra actions.
 */
export const AppDrawer: React.FC<AppDrawerProps> = ({
  title,
  subtitle,
  open = false,
  onClose,
  onSave,
  saveText = "Save",
  saveButtonProps,
  cancelText = "Cancel",
  cancelButtonProps,
  showCancel = true,
  footerLeft,
  extraFooter,
  footer,
  extra,
  width = 560,
  loading = false,
  noPadding = false,
  placement = "right",
  destroyOnClose = true,
  maskClosable = false,
  styles,
  children,
  ...rest
}) => {
  const { token } = theme.useToken();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!onSave) return;
    try {
      setIsSaving(true);
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  const headerExtra = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {extra}
      <Button
        type="text"
        size="small"
        icon={
          <CloseOutlined
            style={{ fontSize: 15, color: token.colorTextSecondary }}
          />
        }
        onClick={onClose}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: token.borderRadiusSM,
        }}
        aria-label="Close"
      />
    </div>
  );

  const headerTitle = title ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span
        style={{
          fontSize: 16,
          fontWeight: 600,
          color: token.colorText,
          lineHeight: 1.3,
        }}
      >
        {title}
      </span>
      {subtitle && (
        <Text
          style={{
            fontSize: 12,
            color: token.colorTextSecondary,
            fontWeight: 400,
            margin: 0,
          }}
        >
          {subtitle}
        </Text>
      )}
    </div>
  ) : undefined;

  const renderFooter = () => {
    if (footer === null) return null;
    if (footer !== undefined) return footer;

    const hasActions = onSave || showCancel || extraFooter || footerLeft;
    if (!hasActions) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: footerLeft ? "space-between" : "flex-end",
          width: "100%",
        }}
      >
        {footerLeft && <div>{footerLeft}</div>}

        <Space size={8}>
          {extraFooter}
          {showCancel && (
            <Button
              onClick={onClose}
              disabled={isSaving || loading}
              style={{
                borderRadius: token.borderRadiusSM,
                minWidth: 76,
              }}
              {...cancelButtonProps}
            >
              {cancelText}
            </Button>
          )}
          {onSave && (
            <Button
              type="primary"
              onClick={handleSave}
              loading={isSaving}
              disabled={loading}
              style={{
                borderRadius: token.borderRadiusSM,
                minWidth: 84,
                fontWeight: 500,
              }}
              {...saveButtonProps}
            >
              {saveText}
            </Button>
          )}
        </Space>
      </div>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement={placement}
      width={width}
      title={headerTitle}
      extra={headerExtra}
      closable={false}
      destroyOnClose={destroyOnClose}
      maskClosable={maskClosable}
      footer={renderFooter()}
      styles={{
        header: {
          padding: "14px 20px",
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        },
        body: {
          padding: noPadding ? 0 : "16px 20px",
          margin: "8px 16px",
          overflowX: "hidden",
        },
        footer: {
          padding: "12px 20px",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        },
        ...styles,
      }}
      {...rest}
    >
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            height: "100%",
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        </div>
      ) : (
        children
      )}
    </Drawer>
  );
};

export default AppDrawer;
